import { TranslationNamespace } from '@/i18n';
import { getTranslated } from '@/i18n/get-translated';

import type { ScenarioKey } from '../constants/settings';

import type { CostGroup } from './calculate';
import { formatRub, formatShortMoney } from './format';

const t = getTranslated(TranslationNamespace.Calculator);

export type ChartScenario = ScenarioKey;

export interface ChartBar {
	label: string;
	lines: [string, string];
	current: number;
	saving: number;
}

export interface ChartSvg {
	viewBox: string;
	html: string;
	ariaLabel: string;
}

const getChartLabels = (): [string, string][] => [
	[t('items.labor'), ''],
	[t('items.storage'), t('items.capital')],
	[t('groups.deferred'), ''],
	[t('groups.losses'), ''],
	[t('groups.marketing'), ''],
	[t('groups.exhibitions'), ''],
];

const COLORS = {
	blue: '#126783',
	orange: '#f2762e',
	grid: '#d7dee8',
	ink: '#33445d',
} as const;

const escapeXml = (value: string) =>
	value.replace(/[&<>'"]/g, (character) => {
		const entities: Record<string, string> = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			"'": '&apos;',
			'"': '&quot;',
		};
		return entities[character];
	});

const barTitle = (bar: ChartBar, series: 'current' | 'saving') =>
	escapeXml(
		`${bar.label}: ${t(series === 'current' ? 'chart.current-short' : 'chart.potential-short')} — ${formatRub(series === 'current' ? bar.current : bar.saving)}`,
	);

export function buildChartData(groups: CostGroup[], scenario: ChartScenario): ChartBar[] {
	const chartLabels = getChartLabels();
	return groups.map((group, index) => ({
		label: group.label,
		lines: chartLabels[index] ?? [group.label, ''],
		current: group.now,
		saving: scenario === 'cons' ? group.consSaving : group.posSaving,
	}));
}

export function buildChartSvg(data: ChartBar[], width: number, scenario: ChartScenario): ChartSvg {
	const compact = width < 650;
	const max = Math.max(1, ...data.flatMap((d) => [d.current, d.saving])) * 1.08;
	const parts: string[] = [];
	let viewBox: string;

	if (compact) {
		const W = 360;
		const H = 560;
		const left = 116;
		const right = 8;
		const top = 34;
		const bottom = 42;
		const plotW = W - left - right;
		const rowH = (H - top - bottom) / data.length;
		viewBox = `0 0 ${W} ${H}`;

		for (let t = 0; t <= 4; t += 1) {
			const x = left + (plotW * t) / 4;
			const val = (max * t) / 4;
			parts.push(
				`<line x1="${x}" y1="${top}" x2="${x}" y2="${H - bottom}" stroke="${COLORS.grid}" stroke-dasharray="5 5"/>`,
			);
			parts.push(
				`<text x="${x}" y="${H - 14}" text-anchor="middle" fill="${COLORS.ink}" font-size="9">${formatShortMoney(val)}</text>`,
			);
		}

		data.forEach((d, i) => {
			const y = top + i * rowH + 8;
			parts.push(
				`<text x="${left - 8}" y="${y + 11}" text-anchor="end" fill="${COLORS.ink}" font-size="10"><tspan x="${left - 8}">${d.lines[0]}</tspan><tspan x="${left - 8}" dy="13">${d.lines[1]}</tspan></text>`,
			);
			parts.push(
				`<rect x="${left}" y="${y}" width="${(plotW * d.current) / max}" height="17" rx="3" fill="${COLORS.blue}"><title>${barTitle(d, 'current')}</title></rect>`,
			);
			parts.push(
				`<rect x="${left}" y="${y + 23}" width="${(plotW * d.saving) / max}" height="17" rx="3" fill="${COLORS.orange}"><title>${barTitle(d, 'saving')}</title></rect>`,
			);
		});
	} else {
		const W = 960;
		const H = 470;
		const left = 70;
		const right = 24;
		const top = 28;
		const bottom = 112;
		const plotW = W - left - right;
		const plotH = H - top - bottom;
		const groupW = plotW / data.length;
		const barW = Math.min(35, groupW * 0.28);
		viewBox = `0 0 ${W} ${H}`;

		for (let t = 0; t <= 4; t += 1) {
			const y = top + plotH - (plotH * t) / 4;
			const val = (max * t) / 4;
			parts.push(
				`<line x1="${left}" y1="${y}" x2="${W - right}" y2="${y}" stroke="${COLORS.grid}" stroke-dasharray="5 5"/>`,
			);
			parts.push(
				`<text x="${left - 10}" y="${y + 5}" text-anchor="end" fill="${COLORS.ink}" font-size="13">${formatShortMoney(val)}</text>`,
			);
		}

		data.forEach((d, i) => {
			const cx = left + groupW * (i + 0.5);
			const currentH = (plotH * d.current) / max;
			const savingH = (plotH * d.saving) / max;
			parts.push(
				`<rect x="${cx - barW - 3}" y="${top + plotH - currentH}" width="${barW}" height="${currentH}" rx="3" fill="${COLORS.blue}"><title>${barTitle(d, 'current')}</title></rect>`,
			);
			parts.push(
				`<rect x="${cx + 3}" y="${top + plotH - savingH}" width="${barW}" height="${savingH}" rx="3" fill="${COLORS.orange}"><title>${barTitle(d, 'saving')}</title></rect>`,
			);
			parts.push(
				`<text x="${cx}" y="${top + plotH + 25}" text-anchor="middle" fill="${COLORS.ink}" font-size="13"><tspan x="${cx}">${d.lines[0]}</tspan><tspan x="${cx}" dy="17">${d.lines[1]}</tspan></text>`,
			);
		});
	}

	const scenarioLabel = t(scenario === 'cons' ? 'chart.conservative' : 'chart.positive');

	return {
		viewBox,
		html: parts.join(''),
		ariaLabel: t('chart.aria', { scenario: scenarioLabel }),
	};
}
